import { ArrowDown, ArrowUp, Minus } from "lucide-react";

interface DeltaBadgeProps {
  /** Percent change. `null` means "no baseline" (e.g. previous period was 0). */
  pct: number | null;
  /** When the metric is "more is worse" (e.g. failed payments), flip the colors. */
  invertColors?: boolean;
}

/**
 * Pill showing period-over-period change with direction arrow + colour.
 * Pure presentation — caller does the math (or gets it from the API).
 */
export function DeltaBadge({ pct, invertColors = false }: DeltaBadgeProps) {
  if (pct === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-text-tertiary">
        <Minus size={9} strokeWidth={2.5} aria-hidden />
        sem base
      </span>
    );
  }

  const rounded = Math.round(pct * 10) / 10;
  const positive = rounded > 0;
  const negative = rounded < 0;
  const isGood = invertColors ? negative : positive;
  const isBad = invertColors ? positive : negative;

  const style = isGood
    ? "bg-accent-soft text-accent"
    : isBad
      ? "bg-danger/10 text-danger"
      : "bg-surface-alt text-text-tertiary";

  const Icon = positive ? ArrowUp : negative ? ArrowDown : Minus;

  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${style}`}
    >
      <Icon size={11} strokeWidth={2.5} aria-hidden />
      {Math.abs(rounded).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
    </span>
  );
}
